import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Copy, Check, Eye, EyeOff, RefreshCw, Loader2 } from 'lucide-react';
import { useToast } from '../components/ui/toast';
import Layout from '../components/Layout';
import { appsAPI, UserApp } from '../services/api';

const AppApiDocs = () => {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [app, setApp] = useState<UserApp | null>(null);
  const [loading, setLoading] = useState(true);
  const [spec, setSpec] = useState<any>(null);
  const [copiedAppId, setCopiedAppId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (appId) {
      fetchAppDetails();
    }
  }, [appId]);

  const fetchAppDetails = async () => {
    try {
      setLoading(true);
      const response = await appsAPI.get(appId!);
      setApp(response.data.data);
      
      // Fetch the OpenAPI spec using authenticated API client
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const specResponse = await fetch(`${baseUrl}/apps/${appId}/openapi-spec`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')!).state.token : ''}`
        }
      });
      
      if (specResponse.ok) {
        const specData = await specResponse.json();
        setSpec(specData);
      } else {
        throw new Error('Failed to fetch OpenAPI spec');
      }
    } catch (error) {
      console.error('Failed to fetch app details:', error);
      addToast({
        title: "Error",
        description: "Failed to load application details.",
        variant: "destructive"
      });
      navigate('/dashboard/apps');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'appId' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'appId') {
        setCopiedAppId(true);
        setTimeout(() => setCopiedAppId(false), 2000);
      } else {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      }
      addToast({
        title: "Copied",
        description: `${type === 'appId' ? 'App ID' : 'Secret Key'} copied to clipboard.`,
        variant: "success"
      });
    } catch (error) {
      addToast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard.",
        variant: "destructive"
      });
    }
  };

  // Custom request interceptor to add app credentials to all requests
  const requestInterceptor = (req: any) => {
    if (app) {
      // Add app credentials to headers for authenticated endpoints
      req.headers['App-Id'] = app.id;
      req.headers['App-Secret'] = app.secret_key;
    }
    return req;
  };

  if (loading) {
    return (
      <Layout title="API Documentation" description="Loading...">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-muted-foreground">Loading API documentation...</p>
        </div>
      </Layout>
    );
  }

  if (!app) {
    return null;
  }

  return (
    <Layout 
      title="API Documentation" 
      description={`Test public APIs for ${app.name}`}
    >
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/apps')}
            className="gap-2 w-full sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Apps
          </Button>
          
          <Button
            variant="outline"
            onClick={fetchAppDetails}
            className="gap-2 w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* App Credentials Card */}
        <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/30">
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Application Credentials</CardTitle>
            <CardDescription className="text-sm">
              These credentials are automatically included in all API requests below.
              Copy them if you need to test outside this interface.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {/* App ID */}
              <div className="space-y-2">
                <label className="text-xs sm:text-sm font-medium text-foreground uppercase tracking-wide">
                  App-Id
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-background border border-border px-3 py-2 rounded break-all leading-relaxed">
                    {app.id}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(app.id, 'appId')}
                    className="h-9 w-9 p-0 shrink-0 touch-target"
                    title="Copy App ID"
                  >
                    {copiedAppId ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Secret Key */}
              <div className="space-y-2">
                <label className="text-xs sm:text-sm font-medium text-foreground uppercase tracking-wide">
                  App-Secret
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-background border border-border px-3 py-2 rounded break-all leading-relaxed">
                    {showSecret ? app.secret_key : '•'.repeat(32)}
                  </code>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowSecret(!showSecret)}
                      className="h-9 w-9 p-0 touch-target"
                      title={showSecret ? "Hide secret" : "Show secret"}
                    >
                      {showSecret ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(app.secret_key, 'secret')}
                      className="h-9 w-9 p-0 touch-target"
                      title="Copy Secret Key"
                    >
                      {copiedSecret ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Swagger UI */}
        <Card>
          <CardContent className="p-0">
            <div className="swagger-wrapper overflow-x-auto">
              {spec && (
                <SwaggerUI
                  spec={spec}
                  requestInterceptor={requestInterceptor}
                  docExpansion="list"
                  defaultModelsExpandDepth={1}
                  defaultModelExpandDepth={1}
                  displayRequestDuration={true}
                  filter={true}
                  showExtensions={true}
                  showCommonExtensions={true}
                  persistAuthorization={true}
                />
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      <style>{`
        .swagger-wrapper {
          padding: 0;
        }
        .swagger-ui .topbar {
          display: none;
        }
        
        /* Hide servers selection */
        .swagger-ui .scheme-container {
          display: none;
        }
        
        /* Hide authorize button */
        .swagger-ui .auth-wrapper,
        .swagger-ui .authorization__btn {
          display: none;
        }
        
        /* Hide filter by tag */
        .swagger-ui .filter-container {
          display: none;
        }
        
        /* Hide OAS version badge and version labels */
        .swagger-ui .info .version,
        .swagger-ui .info hgroup.main a,
        .swagger-ui .info .version-stamp {
          display: none !important;
        }
        
        .swagger-ui .info .title small {
          display: none !important;
        }
        
        .swagger-ui .info .title small.version-stamp {
          display: none !important;
        }
        
        .swagger-ui span.version-stamp {
          display: none !important;
        }
        
        /* Base styles */
        .swagger-ui {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .information-container {
          padding: 1rem;
          background: hsl(var(--card));
        }
        @media (min-width: 640px) {
          .swagger-ui .information-container {
            padding: 2rem;
          }
        }
        
        .swagger-ui .opblock-tag {
          border-bottom: 1px solid hsl(var(--border));
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          color: hsl(var(--foreground));
        }
        @media (min-width: 640px) {
          .swagger-ui .opblock-tag {
            padding: 1rem 2rem;
            font-size: 1rem;
          }
        }
        
        .swagger-ui .opblock {
          margin: 0 0 1rem 0;
          border-radius: 0.5rem;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--card));
        }
        
        .swagger-ui .opblock:focus,
        .swagger-ui .opblock:focus-within {
          outline: none;
          box-shadow: none;
        }
        
        .swagger-ui .opblock-summary {
          padding: 0.75rem 1rem;
        }
        
        .swagger-ui .opblock-summary:focus {
          outline: none;
          box-shadow: none;
        }
        
        .swagger-ui .opblock-summary-control:focus {
          outline: none;
          box-shadow: none;
        }
        @media (min-width: 640px) {
          .swagger-ui .opblock-summary {
            padding: 1rem;
          }
        }
        
        .swagger-ui .opblock-body {
          padding: 1rem;
          background: hsl(var(--background));
          border-radius: 9px;
        }
        
        .swagger-ui section.models {
          padding: 1rem;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
        }
        @media (min-width: 640px) {
          .swagger-ui section.models {
            padding: 2rem;
          }
        }
        
        .swagger-ui .info {
          margin: 0;
        }
        
        .swagger-ui .info .title {
          font-size: 1.25rem;
          font-weight: 600;
          color: hsl(var(--foreground));
        }
        @media (min-width: 640px) {
          .swagger-ui .info .title {
            font-size: 1.5rem;
          }
        }
        
        .swagger-ui .info .description,
        .swagger-ui .info p,
        .swagger-ui .info li {
          color: hsl(var(--muted-foreground));
        }
        
        .swagger-ui .btn.execute {
          background-color: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border: none;
        }
        .swagger-ui .btn.execute:hover {
          background-color: hsl(var(--primary) / 0.9);
        }
        

        
        .swagger-ui table {
          overflow-x: auto;
          display: block;
          background: hsl(var(--card));
        }
        @media (min-width: 640px) {
          .swagger-ui table {
            display: table;
          }
        }
        
        .swagger-ui table thead tr th,
        .swagger-ui table thead tr td {
          color: hsl(var(--foreground));
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .swagger-ui table tbody tr td {
          color: hsl(var(--foreground));
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .swagger-ui .parameters-col_description {
          word-break: break-word;
          color: hsl(var(--foreground)) !important;
          opacity: 0.8;
        }
        
        .swagger-ui .response-col_description {
          word-break: break-word;
          color: hsl(var(--foreground)) !important;
          opacity: 0.8;
        }
        
        .swagger-ui .parameter__type {
          color: hsl(var(--primary)) !important;
        }
        
        .swagger-ui .model-box {
          background: hsl(var(--muted));
          border-radius: 0.375rem;
        }
        
        .swagger-ui .model {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui textarea {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          border: 1px solid hsl(var(--border));
        }
        
        .swagger-ui input[type=text],
        .swagger-ui input[type=password],
        .swagger-ui input[type=search],
        .swagger-ui input[type=email],
        .swagger-ui input[type=file] {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          border: 1px solid hsl(var(--border));
        }
        
        .swagger-ui select {
          background: hsl(var(--background));
          color: hsl(var(--foreground));
          border: 1px solid hsl(var(--border));
        }
        
        .swagger-ui .response-col_status {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .response-col_links {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .parameter__name,
        .swagger-ui .parameter__type {
          color: hsl(var(--foreground)) !important;
          font-weight: 500;
        }
        
        .swagger-ui .parameter__in {
          color: hsl(var(--foreground)) !important;
          opacity: 0.7;
        }
        
        .swagger-ui .parameter__name.required span {
          color: #ef4444 !important;
        }
        
        .swagger-ui .parameter__name.required::after {
          color: #ef4444 !important;
        }
        
        .swagger-ui .response-control-media-type__accept-message {
          color: hsl(var(--muted-foreground));
        }
        
        .swagger-ui .responses-inner h4,
        .swagger-ui .responses-inner h5 {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .opblock-description-wrapper p,
        .swagger-ui .opblock-external-docs-wrapper p,
        .swagger-ui .opblock-title_normal p {
          color: hsl(var(--foreground)) !important;
          opacity: 0.9;
        }
        
        .swagger-ui .opblock-summary-description {
          color: hsl(var(--foreground)) !important;
          opacity: 0.8;
        }
        
        /* API path visibility */
        .swagger-ui .opblock-summary-path {
          color: hsl(var(--foreground)) !important;
          font-weight: 500;
        }
        
        .swagger-ui .opblock-summary-path__deprecated {
          color: hsl(var(--foreground)) !important;
          opacity: 0.6;
        }
        
        .swagger-ui .opblock-summary-method {
          font-weight: 600;
        }
        
        /* Collapse arrow visibility */
        .swagger-ui .opblock-summary-control,
        .swagger-ui .expand-operation svg,
        .swagger-ui svg.arrow {
          fill: hsl(var(--foreground)) !important;
        }
        
        .swagger-ui button.expand-operation svg {
          fill: hsl(var(--foreground)) !important;
        }
        
        .swagger-ui .opblock-section-header {
          background: hsl(var(--muted));
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .tab li {
          color: hsl(var(--muted-foreground));
        }
        
        .swagger-ui .tab li.active {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .opblock-tag-section {
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .swagger-ui .model-title {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .prop-type {
          color: hsl(var(--primary));
        }
        
        .swagger-ui .prop-format {
          color: hsl(var(--muted-foreground));
        }
        

        
        /* Lighter sections - Parameters and Responses headers */
        .swagger-ui .opblock-section-header {
          background: hsl(var(--card)) !important;
          color: hsl(var(--foreground)) !important;
        }
        
        .swagger-ui .opblock-section-header h4 {
          color: hsl(var(--foreground)) !important;
          font-weight: 600;
        }
        
        .swagger-ui .opblock-section-header label {
          color: hsl(var(--foreground)) !important;
        }
        
        /* Try it out button */
        .swagger-ui .btn.try-out__btn {
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          border: 1px solid hsl(var(--primary));
          font-weight: 500;
        }
        
        .swagger-ui .btn.try-out__btn:hover {
          background: hsl(var(--primary) / 0.9);
        }
        
        /* Cancel button */
        .swagger-ui .btn.cancel {
          background: hsl(var(--secondary));
          color: hsl(var(--secondary-foreground));
          border: 1px solid hsl(var(--border));
          font-weight: 500;
        }
        
        .swagger-ui .btn.cancel:hover {
          background: hsl(var(--secondary) / 0.8);
        }
        
        /* Response wrapper */
        .swagger-ui .responses-wrapper {
          background: hsl(var(--card));
        }
        
        /* Highlight code blocks */
        .swagger-ui .highlight-code {
          background: hsl(var(--muted));
        }
        
        .swagger-ui .highlight-code .microlight {
          color: hsl(var(--foreground));
        }
        

        
        /* Loading animation */
        .swagger-ui .loading-container {
          background: hsl(var(--background));
        }
        
        /* Markdown rendering */
        .swagger-ui .markdown p,
        .swagger-ui .markdown code,
        .swagger-ui .markdown pre {
          color: hsl(var(--foreground));
        }
        
        .swagger-ui .markdown code {
          background: hsl(var(--muted));
          color: hsl(var(--primary));
        }
      `}</style>
    </Layout>
  );
};

export default AppApiDocs;
